// @ts-strict-ignore
import pick from 'lodash/pick';

import {getBehaviorShowClauses, hasFunnelMetric, hasRetentionMetric} from 'common/report/insights/models/utils';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {InsightsBookmarkParams, InsightsResourceType} from 'common/types/reports/bookmark';

import {REPORT_LIMITS, ReportName} from '../constants';
import {QueryManager} from '../report-manager/query';
import {BaseQueryErrorType, ParamsAndResults} from '../types';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {CurrentProject} from '../model-resources/projects';
import {InsightsQueryError, InsightsQueryResults} from './util/types';
import {addQueryLimitInfo, isCohortsQuery} from './util';

/** @todo(metrics) these limits should be set in query-api */
export function getQueryLimit(bookmarkParams: InsightsBookmarkParams) {
  return Math.min(
    ...[
      REPORT_LIMITS[ReportName.Insights].query,
      hasFunnelMetric(bookmarkParams) && REPORT_LIMITS[ReportName.Funnels].query,
      hasRetentionMetric(bookmarkParams) && REPORT_LIMITS[ReportName.Retention].query,
    ].filter(Boolean),
  );
}

export class InsightsQueryManager extends QueryManager<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  public reportName = ReportName.Insights;
  public endpoint = `insights`;
  public declare metricsResource: MetricsResource;
  public declare behaviorsResource: BehaviorsResource;

  constructor({
    projectId,
    workspaceId,
    metricsResource,
    behaviorsResource,
  }: {
    projectId: number;
    workspaceId?: number;
    metricsResource: MetricsResource;
    behaviorsResource: BehaviorsResource;
  }) {
    super({projectId, workspaceId});
    this.metricsResource = metricsResource;
    this.behaviorsResource = behaviorsResource;
  }

  public getExtraCsvPostParams({
    bookmarkParams,
    reportQueryOrigin,
    limit,
  }: {
    bookmarkParams: InsightsBookmarkParams;
    reportQueryOrigin: BookmarkType;
    limit: number;
  }) {
    if (
      isCohortsQuery({
        params: bookmarkParams,
        metricsResource: this.metricsResource,
        behaviorsResource: this.behaviorsResource,
      })
    ) {
      return {};
    }

    return {
      queryLimits: {limit},
      report_query_origin: reportQueryOrigin,
    };
  }

  public getExtraPostParams({
    bookmarkParams,
    reportQueryOrigin,
  }: {
    bookmarkParams: InsightsBookmarkParams;
    reportQueryOrigin: BookmarkType;
    currentProject: CurrentProject;
  }) {
    if (
      isCohortsQuery({
        params: bookmarkParams,
        metricsResource: this.metricsResource,
        behaviorsResource: this.behaviorsResource,
      })
    ) {
      return {
        report_query_origin: reportQueryOrigin,
      };
    }

    return {
      report_query_origin: reportQueryOrigin,
    };
  }

  public handleQueryError(err: BaseQueryErrorType): InsightsQueryError {
    return pick(err, [`data`, `status`, `error`, `formula_errors`]) as InsightsQueryError;
  }

  public handleResults({
    params,
    results,
  }: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults>): ParamsAndResults<
    InsightsBookmarkParams,
    InsightsQueryResults
  > {
    const isCohortQuery = getBehaviorShowClauses(params.sections.show).some(
      (show) => show.behavior.resourceType === InsightsResourceType.Cohorts,
    );
    if (!isCohortQuery) {
      results = addQueryLimitInfo(results);
    }

    return {params, results};
  }
}
